import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GithubPicker } from 'react-color';
import Loader from '../Loader';
import { createOrUpdateCodes } from '../../Requests/TextCoderRequests';

const colors_option_2 = ["#ddad9e", "#f8e0c5", "#fcd39e", "#eab976", "#dec487", "#e6dcab", "#f1efb9", "#e2f1b5", "#bae295", "#aeea84", "#bbe4ae", "#aceea4", "#8cdd85", "#85eca4", "#bdf3d7", "#87fdd1", "#6fdcb4", "#a2dcf1", "#8cd2ef", "#aad2ec", "#bedcf0", "#72b0db", "#9bbbfc", "#a4a3dd", "#a8a1e9", "#c7bdf2", "#b29fdf", "#d789fe", "#e5a3f6", "#f4bff8", "#e895b1", "#f597a4"]

function TextCodeModal({ handleClose, code, handleUpdateCode, handleAddNewCode, textCodes }) {
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const { project_uuid } = useParams();
    const [uploading, setUploading] = useState(false);
    const [hex, setHex] = useState(code?.color || "#f67088");

    const availableParentCodes = textCodes?.filter(c =>
        !c.parent &&
        (!code || c.uuid !== code.uuid)
    ) || [];

    const hasChildren = textCodes?.some(c => c.parent === code?.uuid);

    const updateColor = (color) => {
        setHex(color.hex);
    }

    const closeModal = () => {
        handleClose();
        setOpen(false);
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setUploading(true);

        const data = {
            name: event.target.code_name.value,
            description: event.target.code_description.value,
            project_id: project_uuid,
            color: hex,
            parent: hasChildren ? null : (event.target?.parent_code?.value || null),
            ...(code?.uuid && { uuid: code.uuid, excerpts: code.excerpts })
        };

        const request = code?.uuid ?
            createOrUpdateCodes(data).then(res => handleUpdateCode(data)) :
            createOrUpdateCodes(data).then(res => handleAddNewCode(res.data));

        request
            .then(() => handleClose())
            .catch(e => console.error(e))
            .finally(() => setUploading(false));
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div data-testid="text-code-modal">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center">
                                        {t('TextCoder.Create code')}
                                    </Dialog.Title>

                                    <form className="mt-4 space-y-6" onSubmit={handleSubmit} data-testid="text-code-form">
                                        <div>
                                            <label htmlFor="code_name" className="block text-sm font-medium text-gray-900">
                                                {t('TextCoder.Code name')}
                                            </label>
                                            <input
                                                type="text"
                                                name="code_name"
                                                id="code_name"
                                                defaultValue={code?.name}
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                                data-testid="text-code-name-input"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="code_description" className="block text-sm font-medium text-gray-900">
                                                {t('TextCoder.Code description')}
                                            </label>
                                            <textarea
                                                name="code_description"
                                                id="code_description"
                                                defaultValue={code?.description}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                                data-testid="text-code-description-input"
                                            />
                                        </div>

                                        {availableParentCodes.length > 0 && (
                                            <div>
                                                <label htmlFor="parent_code" className="block text-sm font-medium text-gray-900">
                                                    {t('TextCoder.Parent code')}
                                                </label>
                                                <select
                                                    name="parent_code"
                                                    id="parent_code"
                                                    defaultValue={code?.parent || ""}
                                                    disabled={hasChildren}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                >
                                                    <option value="">{t('TextCoder.No parent')}</option>
                                                    {availableParentCodes.map(parentCode => (
                                                        <option key={parentCode.uuid} value={parentCode.uuid}>
                                                            {parentCode.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {hasChildren && (
                                                    <p className="mt-1 text-sm text-red-600">
                                                        {t('TextCoder.HasChildrenWarning', 'This code has subcodes, and cannot be part of another code')}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">
                                                {t('TextCoder.Color')}
                                            </label>
                                            <div className="mt-2 flex justify-center" data-testid="text-code-color-picker">
                                                <GithubPicker
                                                    width="full"
                                                    triangle="hide"
                                                    color={hex}
                                                    onChangeComplete={updateColor}
                                                    colors={colors_option_2}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex justify-between gap-4 pt-4">
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                                data-testid="text-code-cancel-button"
                                            >
                                                {t('Audio.AudioUpload.Step2.BackButton')}
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={uploading}
                                                className="rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-400"
                                                data-testid="text-code-save-button"
                                            >
                                                {uploading ? <Loader /> : t('Audio.AudioUpload.Step2.NextButton')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default TextCodeModal;
